import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, ShoppingBag, Clock, CheckCircle, XCircle, 
  LogOut, ArrowLeft, Loader2, AlertCircle, CreditCard,
  Package, Calendar, Euro
} from 'lucide-react';
import { useClient } from '../../contexts/ClientContext';
import { useLanguage } from '../../contexts/LanguageContext';
import * as clientApi from '../../utils/clientApi';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { client, logout, isAuthenticated, loading: authLoading } = useClient();
  const { language } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const translations = {
    en: {
      title: 'My Account',
      welcome: 'Welcome back',
      myOrders: 'My Orders',
      noOrders: 'No orders yet',
      noOrdersDesc: 'Your orders will appear here after your first purchase.',
      browseServices: 'Browse Services',
      orderNumber: 'Order',
      status: 'Status',
      total: 'Total',
      date: 'Date',
      viewDetails: 'View Details',
      orderDetails: 'Order Details',
      items: 'Items',
      paymentStatus: 'Payment Status',
      orderStatus: 'Order Status',
      close: 'Close',
      logout: 'Sign Out',
      backToSite: 'Back to Website',
      statuses: {
        pending: 'Pending',
        processing: 'In Progress',
        completed: 'Completed',
        cancelled: 'Cancelled'
      },
      paymentStatuses: {
        pending: 'Payment Pending',
        completed: 'Paid',
        failed: 'Payment Failed'
      }
    },
    es: {
      title: 'Mi Cuenta',
      welcome: 'Bienvenido',
      myOrders: 'Mis Pedidos',
      noOrders: 'Sin pedidos todavía',
      noOrdersDesc: 'Tus pedidos aparecerán aquí después de tu primera compra.',
      browseServices: 'Ver Servicios',
      orderNumber: 'Pedido',
      status: 'Estado',
      total: 'Total',
      date: 'Fecha',
      viewDetails: 'Ver Detalles',
      orderDetails: 'Detalles del Pedido',
      items: 'Artículos',
      paymentStatus: 'Estado del Pago',
      orderStatus: 'Estado del Pedido',
      close: 'Cerrar',
      logout: 'Cerrar Sesión',
      backToSite: 'Volver al Sitio',
      statuses: {
        pending: 'Pendiente',
        processing: 'En Progreso',
        completed: 'Completado',
        cancelled: 'Cancelado'
      },
      paymentStatuses: {
        pending: 'Pago Pendiente',
        completed: 'Pagado',
        failed: 'Pago Fallido'
      }
    },
    de: {
      title: 'Mein Konto',
      welcome: 'Willkommen zurück',
      myOrders: 'Meine Bestellungen',
      noOrders: 'Noch keine Bestellungen',
      noOrdersDesc: 'Ihre Bestellungen erscheinen hier nach Ihrem ersten Kauf.',
      browseServices: 'Dienste durchsuchen',
      orderNumber: 'Bestellung',
      status: 'Status',
      total: 'Gesamt',
      date: 'Datum',
      viewDetails: 'Details anzeigen',
      orderDetails: 'Bestelldetails',
      items: 'Artikel',
      paymentStatus: 'Zahlungsstatus',
      orderStatus: 'Bestellstatus',
      close: 'Schließen',
      logout: 'Abmelden',
      backToSite: 'Zurück zur Website',
      statuses: {
        pending: 'Ausstehend',
        processing: 'In Bearbeitung',
        completed: 'Abgeschlossen',
        cancelled: 'Storniert'
      },
      paymentStatuses: {
        pending: 'Zahlung ausstehend',
        completed: 'Bezahlt',
        failed: 'Zahlung fehlgeschlagen'
      }
    }
  };

  const t = translations[language] || translations.es;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/client/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
    }
  }, [isAuthenticated]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await clientApi.getClientOrders();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      processing: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      processing: Package,
      completed: CheckCircle,
      cancelled: XCircle
    };
    const Icon = icons[status] || Clock;
    return <Icon className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(
      language === 'es' ? 'es-ES' : language === 'de' ? 'de-DE' : 'en-US',
      { year: 'numeric', month: 'short', day: 'numeric' }
    );
  };

  if (authLoading || (!isAuthenticated && !authLoading)) {
    return (
      <div className="min-h-screen bg-[#141619] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const handleBackToSite = () => {
    navigate('/');
  };

  const handleBrowseServices = () => {
    navigate('/');
    // Scroll to services section after navigation
    setTimeout(() => {
      const element = document.getElementById('services');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-[#141619]" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-[#1a1d21] border-b border-[#2a2d32]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={handleBackToSite}
            className="flex items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{t.backToSite}</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#9CA3AF] hover:text-red-400 transition-colors"
            data-testid="client-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">{t.logout}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t.title}</h1>
          <p className="text-[#9CA3AF]">
            {t.welcome}, <span className="text-[#D4AF37]">{client?.name}</span>
          </p>
        </div>

        {/* Orders Section */}
        <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] overflow-hidden">
          <div className="p-6 border-b border-[#2a2d32]">
            <h2 className="text-xl font-semibold text-white flex items-center gap-3">
              <ShoppingBag className="w-6 h-6 text-[#D4AF37]" />
              {t.myOrders}
            </h2>
          </div>

          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <ShoppingBag className="w-16 h-16 text-[#3a3d42] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">{t.noOrders}</h3>
              <p className="text-[#9CA3AF] mb-6">{t.noOrdersDesc}</p>
              <button
                onClick={handleBrowseServices}
                className="inline-flex items-center px-6 py-3 bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619] rounded-lg font-medium transition-colors"
              >
                {t.browseServices}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[#2a2d32]">
              {orders.map((order) => (
                <div
                  key={order.order_id}
                  className="p-6 hover:bg-[#2a2d32]/30 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-white font-medium">
                          {t.orderNumber} #{order.order_id.substring(0, 8).toUpperCase()}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(order.order_status)}`}>
                          {getStatusIcon(order.order_status)}
                          {t.statuses[order.order_status] || order.order_status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-[#9CA3AF]">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(order.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <CreditCard className="w-4 h-4" />
                          {t.paymentStatuses[order.payment_status] || order.payment_status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xl font-bold text-[#D4AF37]">
                        €{order.total?.toFixed(2)}
                      </span>
                      <Button
                        onClick={() => setSelectedOrder(order)}
                        variant="outline"
                        className="border-[#2a2d32] text-white hover:bg-[#2a2d32]"
                        data-testid={`view-order-${order.order_id}`}
                      >
                        {t.viewDetails}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] w-full max-w-lg max-h-[80vh] overflow-auto">
            <div className="p-6 border-b border-[#2a2d32] flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">{t.orderDetails}</h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 hover:bg-[#2a2d32] rounded-lg"
              >
                <XCircle className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order ID & Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#9CA3AF] mb-1">{t.orderNumber}</p>
                  <p className="text-white font-mono">#{selectedOrder.order_id.substring(0, 8).toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-sm text-[#9CA3AF] mb-1">{t.date}</p>
                  <p className="text-white">{formatDate(selectedOrder.created_at)}</p>
                </div>
              </div>

              {/* Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#9CA3AF] mb-1">{t.orderStatus}</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.order_status)}`}>
                    {getStatusIcon(selectedOrder.order_status)}
                    {t.statuses[selectedOrder.order_status] || selectedOrder.order_status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-[#9CA3AF] mb-1">{t.paymentStatus}</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                    selectedOrder.payment_status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {selectedOrder.payment_status === 'completed' ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    {t.paymentStatuses[selectedOrder.payment_status] || selectedOrder.payment_status}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div>
                <p className="text-sm text-[#9CA3AF] mb-3">{t.items}</p>
                <div className="space-y-2">
                  {(selectedOrder.items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-[#2a2d32] last:border-0">
                      <div>
                        <p className="text-white">{item.service_name}</p>
                        <p className="text-sm text-[#6B7280]">x{item.quantity}</p>
                      </div>
                      <p className="text-[#D4AF37] font-medium">€{item.total?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="pt-4 border-t border-[#2a2d32] flex justify-between items-center">
                <span className="text-lg font-medium text-white">{t.total}</span>
                <span className="text-2xl font-bold text-[#D4AF37]">€{selectedOrder.total?.toFixed(2)}</span>
              </div>

              <Button
                onClick={() => setSelectedOrder(null)}
                className="w-full bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]"
              >
                {t.close}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;
